import React, { Children, useEffect, useRef } from "react";
import styled from "styled-components";
import { Dialog, Icon } from "@mui/material";
import useIsMobileDevice from "../../hooks/useIsMobileDevice";
import useDialogBackButton from "../../hooks/useDialogBackButton";
import MUIButton from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";
import Button from "./Button";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import makeStyles from "@mui/styles/makeStyles";

const useStyles = makeStyles(() => {
  return {
    popup: {
      "& .MuiPaper-root": {
        maxWidth: "auto",
      },
    },
  };
});

const getBreakPointWidth = (breakpoints, size) => {
  const idx = breakpoints.keys.indexOf(size);
  if (idx < 0) {
    return null;
  }
  const key = breakpoints.keys[idx + 1];

  if (!key) {
    return false;
  }
  return breakpoints.values[key];
};

const PopupContainer = styled.div`
  background-color: white;
  color: black;
  position: relative;
  box-sizing: border-box;
  ${(props) => {
    console.log("The mui theme", props.theme);
    if (props.maxWidth) {
      const breakpoints = props?.theme?.MUITheme?.breakpoints || {
        keys: ["xs", "sm", "md", "lg", "xl"],
        values: {
          xs: 0,
          sm: 600,
          md: 900,
          lg: 1200,
          xl: 1536,
        },
      };
      const widthInPixels = getBreakPointWidth(breakpoints, props.maxWidth);
      if (widthInPixels) {
        console.log("We got max width", props.maxWidth, widthInPixels);
        return (
          "max-width: " +
          widthInPixels +
          "px;\n" +
          "max-height: calc(90vh - 60px);\n"
        );
      }
    } else if (props.fullScreen) {
      console.log("Popup: Fullscreen");
      return `width: 100%;  
                height: 100%;                `;
    } else {
      console.log("Popup: If not, max height?");
      return "max-height: calc(90vh - 60px);\n" + "margin: 16px;\n";
    }
  }}
  ${(props) => {
    if (props.fullWidth) {
      console.log("Popup: Full width");
      return "width: 100% \n";
    }
  }}
  display: flex;
  flex-direction: column;
`;

PopupContainer.defaultProps = {
  maxWidth: "md",
};

const CloseButton = styled.button`
  position: absolute;
  right: 0;
  top: 0;
  background-color: white;
  padding: 10px;
  color: rgba(0, 0, 0, 0.7);
  cursor: pointer;
  border: none;
`;

const GoBackButton = styled(CloseButton)`
  right: auto;
  left: 0;
`;

let idCounter = 0;

const Popup = (props) => {
  const titleId = useRef(`popup-title-${++idCounter}`);
  const [t] = useTranslation();
  const classes = useStyles();

  // Let the hardware/browser back button close this dialog instead of
  // navigating off the page. Prefer the explicit close handler, falling back
  // to the backdrop handler; if the dialog is non-dismissable, stay inert.
  const dismiss = props.onClose || props.onBackdropPress;
  const { consumeClose } = useDialogBackButton(
    Boolean(props.open) && Boolean(dismiss),
    dismiss,
  );
  useEffect(() => {
    if (!props.consumeCloseRef) {
      return undefined;
    }

    props.consumeCloseRef.current = consumeClose;
    return () => {
      if (props.consumeCloseRef.current === consumeClose) {
        props.consumeCloseRef.current = null;
      }
    };
  }, [consumeClose, props.consumeCloseRef]);

  if (!props.open) {
    return null;
  }
  let onClose = null;
  if (props.onClose) {
    onClose = (
      <CloseButton
        role="button"
        aria-label={t("Close dialog")}
        onClick={(e) => props.onClose()}
      >
        <Icon>close</Icon>
      </CloseButton>
    );
  }
  let onGoBack = null;
  if (props.onGoBack) {
    onGoBack = (
      <GoBackButton href onClick={(e) => props.onGoBack()}>
        <Icon>arrow_back</Icon>
      </GoBackButton>
    );
  }

  console.log("Popup on close", onClose);

  let hasTitle = false;
  // Add id to title field if present, so we can use aria labeled by

  // Forward data-testid to the element that carries role="dialog", so
  // callers can identify a specific dialog with getByTestId(...). The
  // remaining props get spread onto PopupContainer below, so strip it
  // from there to avoid the same testid appearing on two elements.
  const { "data-testid": testId, ...containerProps } = props;

  return (
    <Dialog
      onClose={props.onBackdropPress}
      open={true}
      maxWidth={false}
      PaperProps={{
        style: {
          maxWidth: "auto",
          width: "fit-content",
        },
        "data-testid": testId,
      }}
    >
      <PopupContainer
        {...(hasTitle ? { "aria-labelledby": titleId.current } : {})}
        onClick={(e) => e.stopPropagation()}
        {...containerProps}
      >
        {props.children}
        {onGoBack}
        {onClose}
      </PopupContainer>
    </Dialog>
  );
};

Popup.defaultProps = {
  open: true,
};

export const PopupHeadingContainer = styled.div`
  padding-top: 48px;
  padding-left: 30px;
  padding-right: 30px;
  padding-bottom: 30px;
`;

const StyledPopupHeading = styled.h2`
  /*margin-bottom: 16px; */
  font-size: 20px;
  text-align: center;
  font-weight: 500;
  margin: 0;
  color: ${(props) =>
    props.theme?.colors.primary.dark.color || "black"} !important;
`;

const PopupHeading = ({ children, id, subHeading }) => {
  return (
    <PopupHeadingContainer>
      <StyledPopupHeading id={id}>{children}</StyledPopupHeading>
      {subHeading && <PopupSubHeading>{subHeading}</PopupSubHeading>}
    </PopupHeadingContainer>
  );
};

PopupHeading.isPopupHeading = true;

PopupHeading.defaultProps = {
  subHeading: null,
  id: undefined,
};

PopupHeading.propTypes = {
  subHeading: PropTypes.string,
  id: PropTypes.string,
};

const PopupSubHeading = styled.h2`
  margin-top: 0;
  margin-bottom: 0;
  font-size: 16px;
  color: #595959;
  text-align: center;
`;

const PopupContent = styled.div`
  margin-bottom: 16px;
  flex-grow: 1;
  flex-shrink: 1;
  overflow: auto;
  padding: 16px 30px;
  font-weight: 400;
  p {
    margin-top: 0;
  }
`;

const StyledPopupActions = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: ${(props) => {
    console.log("The props children", props.children);
    return Children.count(props.children) == 1 ? "flex-end" : "space-between";
  }};
  > button,
  a {
    margin-left: 8px;
  }

  padding: ${(props) => (props.isMobile ? "15px" : "40px")};
  padding-left: ${(props) => (props.isMobile ? "15px" : "64px")};
  padding-right: ${(props) => (props.isMobile ? "15px" : "64px")};
  /* Remove padding and spacing so they align with content */
  .MuiButton-text:first-child {
    margin-left: 0;
    padding-left: 0;
  }
  .MuiButton-text:last-child {
    margin-right: 0;
    padding-right: 0;
  }
`;

const PopupActions = (props) => {
  const isMobile = useIsMobileDevice();
  return <StyledPopupActions isMobile={isMobile} {...props} />;
};

const PopupTextContent = styled.div`
  margin-top: ${(props) => props.theme.MUITheme.spacing(4)};
  margin-bottom: ${(props) => props.theme.MUITheme.spacing(4)};
  text-align: center;
`;

const PopupButton = ({ secondary = false, ...props }) => {
  const theme = useTheme();

  const extraProps = {};
  if (theme.clientType == "desktop") {
    extraProps.variant = "primary";
    if (secondary) {
      extraProps.inverted = true;
    }
    return <Button {...extraProps} {...props} />;
  }
  extraProps.color = "primary";
  return <MUIButton {...extraProps} {...props} />;
};

PopupButton.propTypes = {
  secondary: PropTypes.bool,
};

export {
  Popup,
  PopupHeading,
  PopupSubHeading,
  PopupActions,
  PopupContent,
  PopupTextContent,
  PopupButton,
};

export default Popup;
